import { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController
);

export default function IntensityAnimation({
  intensity,
  segment,
  broadcastTime,
  showIntensity = true,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [showWordLabels, setShowWordLabels] = useState(true);

  // Calculate wordIntervals first
  const wordIntervals = segment?.words?.map((w) => ({
    start: w.start - segment.start,
    end: w.end - segment.start,
    text: w.text,
  })) || [];

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setShowWordLabels(!mobile && wordIntervals.length <= 12);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [wordIntervals.length]);

  const xs = intensity?.xs || [];
  const ys = intensity?.ys || [];

  if (!xs.length) return null;

  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const yRange = yMax - yMin;

  const xMin = xs[0];
  const xMax = xs[xs.length - 1];
  const cursorX = Math.min(Math.max(broadcastTime ?? xMin, xMin), xMax);

  // 🔹 Local maxima for intensity
  const localMaxima = [];
  for (let i = 1; i < ys.length - 1; i++) {
    if (ys[i] > ys[i - 1] && ys[i] > ys[i + 1]) {
      localMaxima.push({ x: xs[i], y: ys[i] });
    }
  }

  const datasets = [];

  // 🔷 Main intensity line - optimized for mobile
  if (showIntensity) {
    datasets.push({
      label: "Intensity (dB)",
      data: xs.map((x, i) => ({ x, y: ys[i] })),
      borderColor: "#2563eb",
      borderWidth: isMobile ? 1.5 : 2,
      tension: isMobile ? 0.2 : 0.4,
      pointRadius: 0,
      showLine: true,
      datalabels: { display: false },
    });
  }

  // 🔴 Local maxima - with connecting line (RED LINE)
  datasets.push({
    label: "Local Maxima",
    data: localMaxima,
    borderColor: "red",
    borderWidth: isMobile ? 1 : 2,
    tension: isMobile ? 0.2 : 0.4,
    pointRadius: isMobile ? 2 : 4,
    pointBackgroundColor: "red",
    pointBorderColor: "red",
    showLine: true, // This creates the connecting red line
    datalabels: { display: false },
  });

  // 🔹 Word boundaries - thinner on mobile
  wordIntervals.forEach((w) => {
    const extend = yRange * 0.1;
    datasets.push({
      label: `start-${w.text}`,
      data: [
        { x: w.start, y: yMin - extend },
        { x: w.start, y: yMax },
      ],
      borderColor: "rgba(200,0,0,0.3)",
      borderWidth: isMobile ? 0.5 : 1,
      pointRadius: 0,
      showLine: true,
      datalabels: { display: false },
    });
  });

  // ✅ Active word cursor
  const active = wordIntervals.find(
    (w) => cursorX >= w.start && cursorX <= w.end
  );
  if (active) {
    datasets.push({
      label: "Active interval",
      type: "line",
      data: [
        { x: active.start, y: yMin - yRange * 0.1 },
        { x: active.end, y: yMin - yRange * 0.1 },
        { x: active.end, y: yMax },
        { x: active.start, y: yMax },
        { x: active.start, y: yMin - yRange * 0.1 },
      ],
      borderColor: "rgba(16,185,129,0.8)",
      backgroundColor: "rgba(16,185,129,0.35)",
      fill: true,
      pointRadius: 0,
      showLine: true,
      tension: 0,
      datalabels: { display: false },
    });
  }

  // ✅ Moving broadcast cursor
  const extend = yRange * 0.1;
  datasets.push({
    label: "Cursor",
    data: [{ x: cursorX, y: yMin - extend }, { x: cursorX, y: yMax }],
    borderColor: "rgba(16,185,129,1)",
    borderWidth: isMobile ? 1.5 : 2,
    pointRadius: 0,
    showLine: true,
    spanGaps: true,
    datalabels: { display: false },
  });

  // 🏷 Word labels — only on desktop and when reasonable number of words
  const shouldShowWordLabels = showWordLabels && wordIntervals.length > 0 && wordIntervals.length <= 12;
  
  if (shouldShowWordLabels) {
    datasets.push({
      label: "Word labels",
      type: "scatter",
      data: wordIntervals.map((w) => ({
        x: (w.start + w.end) / 2,
        y: yMin + yRange * 0.01,
        text: w.text,
      })),
      pointRadius: 0,
      datalabels: {
        display: true,
        color: "#111",
        font: { 
          weight: "bold", 
          size: isMobile ? 10 : 12 
        },
        formatter: (v) => v.text,
        align: "bottom",
        anchor: "end",
        clip: false,
        yAdjust: isMobile ? 4 : 6,
      },
    });
  }

  const data = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: { 
        type: "linear", 
        grid: { display: false }, 
        ticks: { display: false },
        title: { display: !isMobile, text: "Time (seconds)" }
      },
      y: {
        min: yMin - yRange * 0.15,
        max: yMax,
        title: { 
          display: true, 
          text: "Intensity (dB)",
          font: { size: isMobile ? 12 : 14 }
        },
        ticks: {
          font: { size: isMobile ? 10 : 12 }
        }
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: !isMobile,
        mode: 'nearest',
        intersect: false
      },
      datalabels: { clip: false },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {/* Word labels toggle for mobile */}
      {isMobile && wordIntervals.length > 0 && (
        <label className="flex items-center gap-2 select-none text-xs">
          <input
            type="checkbox"
            checked={showWordLabels}
            onChange={() => setShowWordLabels(!showWordLabels)}
            className="scale-90"
          />
          Show word labels
        </label>
      )}

      {/* 📉 Chart wrapper with better mobile sizing */}
      <div className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
        isMobile ? 'h-48' : 'h-64'
      }`}>
        <Line data={data} options={options} />
      </div>

      {/* Mobile info text */}
      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Pinch to zoom • Word labels: {showWordLabels ? 'ON' : 'OFF'}
        </p>
      )}
    </div>
  );
}